import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.js';
import { api } from '../api.js';
import { applyBranding } from '../utils/branding.js';
import { useI18nStore } from '../store/i18n.js';
import { useT } from '../i18n/useT.js';
import { EN_MESSAGES } from '../i18n/messages/en.js';
import { mergeDeep } from '../i18n/mergeMessages.js';
import {
  FileText, Image, Code, Clock, FileOutput, Settings, LogOut, Users, DollarSign, CreditCard,
  LayoutDashboard, UserCircle2
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', labelKey: 'nav.dashboard', labelFallback: 'Dashboard', icon: LayoutDashboard },
  { to: '/profile', labelKey: 'nav.subscriptionPlan', labelFallback: 'Suscripción & plan', icon: UserCircle2 },
  { to: '/templates', labelKey: 'nav.templates', labelFallback: 'Templates', icon: FileText },
  { to: '/assets', labelKey: 'nav.assets', labelFallback: 'Assets', icon: Image },
  { to: '/scripts', labelKey: 'nav.scripts', labelFallback: 'Scripts', icon: Code },
  { to: '/jobs', labelKey: 'nav.jobs', labelFallback: 'Jobs', icon: Clock },
  { to: '/reports', labelKey: 'nav.reports', labelFallback: 'Reports', icon: FileOutput },
  { to: '/users', labelKey: 'nav.users', labelFallback: 'Users', icon: Users },
  { to: '/admin-settings', labelKey: 'nav.adminSettings', labelFallback: 'Admin Settings', icon: Settings },
  { to: '/plans', labelKey: 'nav.plans', labelFallback: 'Plans', icon: DollarSign },
  { to: '/billing-settings', labelKey: 'nav.billingSettings', labelFallback: 'Billing', icon: CreditCard },
  { to: '/settings', labelKey: 'nav.settings', labelFallback: 'Settings', icon: Settings },
];

export default function Layout({ children }) {
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const setBundle = useI18nStore((s) => s.setBundle);
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoText, setLogoText] = useState('Report Engine');
  const [logoIconUrl, setLogoIconUrl] = useState(null);
  const [studioAllowed, setStudioAllowed] = useState(null);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const studioBlocked = studioAllowed === false;
  const visibleNavItems = navItems.filter((item) => {
    if ((item.to === '/users' || item.to === '/admin-settings' || item.to === '/plans' || item.to === '/billing-settings') && !isAdmin) return false;
    if (studioBlocked && item.to !== '/profile') return false;
    return true;
  });

  useEffect(() => {
    const run = async () => {
      try {
        const [branding, lang, billing] = await Promise.all([
          api.getAdminSettings(),
          api.getCurrentLanguage(),
          api.getMyBilling(),
        ]);
        applyBranding(branding);
        setLogoText(branding.logoText || branding.appName || 'Report Engine');
        setLogoIconUrl(branding.logoIconUrl || null);
        setBundle({
          locale: lang.activeCode || 'en',
          messages: mergeDeep(EN_MESSAGES, lang.messages || {}),
          available: lang.available || [{ code: 'en', name: 'English', is_default: true }],
        });
        setStudioAllowed(billing?.features?.studio !== false);
      } catch {
        setLogoText('Report Engine');
        setLogoIconUrl(null);
        setStudioAllowed(true);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (studioAllowed === false && location.pathname !== '/profile') {
      navigate('/profile?blocked=studio', { replace: true });
    }
  }, [studioAllowed, location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 52 }}>
          {logoIconUrl ? (
            <img
              src={logoIconUrl}
              alt="App logo"
              style={{ maxWidth: '100%', maxHeight: 44, width: 'auto', height: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <span>{logoText}</span>
          )}
        </div>
        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <item.icon size={18} />
              {t(item.labelKey, item.labelFallback)}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div>{user?.email || 'admin@localhost'}</div>
          {studioBlocked && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--warning, #f59e0b)' }}>{t('billing.studio', 'Studio Access')} {t('billing.planInactive', 'Inactive')}</div>}
          <button
            onClick={handleLogout}
            style={{ background: 'none', color: 'var(--text-muted)', padding: '4px 0', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <LogOut size={14} /> {t('nav.logout', 'Logout')}
          </button>
        </div>
      </aside>
      <main className="main-content">
        {studioAllowed === null ? (
          <div className="page-container"><p>{t('billing.loading', 'Loading...')}</p></div>
        ) : (studioBlocked && location.pathname !== '/profile') ? (
          <div className="page-container"><p>{t('billing.studio', 'Studio Access')} {t('billing.planInactive', 'Inactive')}</p></div>
        ) : children}
      </main>
    </div>
  );
}
